import {PhoneNumber, PhoneNumberFormat, PhoneNumberUtil} from 'google-libphonenumber';
import {environment} from '@environment';

enum DisplayFormat {
  DISPLAY,
  BACKEND,
  E164
}

export class GtPhoneNumberUtil {
  private static countryCode: number;

  public static toDisplay(phoneNumber: string): string {
    return GtPhoneNumberUtil.format(phoneNumber, DisplayFormat.DISPLAY);
  }

  public static toE164(phoneNumber: string): string {
    return GtPhoneNumberUtil.format(phoneNumber, DisplayFormat.E164);
  }

  public static toBackend(phoneNumber: string): string {
    return GtPhoneNumberUtil.format(phoneNumber, DisplayFormat.BACKEND);
  }

  private static format(phoneNumber: string, format: DisplayFormat): string {
    if (phoneNumber) {
      try {
        const util = PhoneNumberUtil.getInstance();
        const parsedNumber = util.parse(phoneNumber, environment.phoneNumberFormatLocalization);
        if (util.isValidNumberForRegion(parsedNumber, environment.phoneNumberFormatLocalization)) {
          return GtPhoneNumberUtil.formatSpecific(parsedNumber, format, PhoneNumberFormat.NATIONAL);
        } else if (util.isValidNumber(parsedNumber)) {
          return GtPhoneNumberUtil.formatSpecific(parsedNumber, format, PhoneNumberFormat.INTERNATIONAL);
        } else {
          if (phoneNumber.indexOf('+') !== 0) {
            phoneNumber = '+' + phoneNumber;
            return GtPhoneNumberUtil.format(phoneNumber, format);
          } else {
            return phoneNumber.slice(1, phoneNumber.length);
          }
        }
      } catch (e) {
        if ('anonymous' !== phoneNumber) {
          console.log('format::Exception when parsing number ' + phoneNumber);
        }
        return phoneNumber;
      }
    } else {
      return '';
    }
  }

  private static formatSpecific(parsedNumber: PhoneNumber, format: DisplayFormat, phoneNumberFormat: PhoneNumberFormat): string {
    const util = PhoneNumberUtil.getInstance();
    switch (format) {
      case DisplayFormat.DISPLAY:
        return util.format(parsedNumber, phoneNumberFormat);
      case DisplayFormat.E164:
        return util.format(parsedNumber, PhoneNumberFormat.E164);
      case DisplayFormat.BACKEND:
        const formatted = util.format(parsedNumber, PhoneNumberFormat.E164);
        return formatted.slice(1, formatted.length);
      default:
        return '-- unknown --';
    }
  }

  public static isSameNumber(searchString: string, phoneNumber: string): boolean {
    // Return false if search string contains letters
    if (isNaN(+searchString) || phoneNumber == null) {
      return false;
    }
    // Remove spaces, letters, special letters and leading zeroes
    const phoneNumberOnlyNumbers = phoneNumber.replace(/[^0-9]/g, '').toString();

    // Remove all non-digits from the output of toDisplay(), and then converts it to a number to remove leading zeroes,
    // before converting it back to a string so we can use it as a sub-string search.
    searchString = (+GtPhoneNumberUtil.toDisplay(searchString).replace(/[^0-9]/g, '')).toString();

    return phoneNumberOnlyNumbers.indexOf(searchString) > -1;
  }

  public static isValidNumber(phoneNumber: string): boolean {
    const util = PhoneNumberUtil.getInstance();
    try {
      const parsedNumber = util.parse(phoneNumber, environment.phoneNumberFormatLocalization);
      const parsedCountryCode = parsedNumber.getCountryCode().toString();
      let isValid = util.isValidNumberForRegion(parsedNumber) || util.isValidNumber(parsedNumber);

      //strip country code only for local numbers
      const numberWithoutCountryCode =
        parsedCountryCode === GtPhoneNumberUtil.getCountryCode().toString()
          ? GtPhoneNumberUtil.stripCountryCode(phoneNumber).toString().trim()
          : '';

      //check length only for Norwegian and Romanian numbers, otherwise use only validation above
      if (parsedCountryCode === '47' && numberWithoutCountryCode) {
        isValid = isValid ||
          (numberWithoutCountryCode.length >= 8 &&
            numberWithoutCountryCode.length <= 19 &&
            numberWithoutCountryCode.match(/^[0-9]+$/) !== null);
      } else if (parsedCountryCode === '40' && parsedNumber.getNationalNumber()) {
        const nationalNumber = parsedNumber.getNationalNumber().toString();
        isValid = nationalNumber.length > 8 &&
          nationalNumber.length <= 19 &&
          nationalNumber.match(/^[0-9]+$/) !== null;
      }

      return isValid;
    } catch (e) {
      if ('anonymous' !== phoneNumber) {
        console.log('isValidNumber::Exception when parsing number ' + phoneNumber);
      }
      return false;
    }
  }

  private static getCountryCode(): number {
    if (!GtPhoneNumberUtil.countryCode) {
      GtPhoneNumberUtil.countryCode = PhoneNumberUtil.getInstance().getCountryCodeForRegion(environment.phoneNumberFormatLocalization);
    }
    return GtPhoneNumberUtil.countryCode;
  }

  private static stripCountryCode(phoneNumber: string): string {
    const countryCode = phoneNumber.startsWith('00') ? `00${GtPhoneNumberUtil.countryCode}` : `+${GtPhoneNumberUtil.countryCode}`;
    if (phoneNumber?.indexOf(countryCode) === 0) {
      phoneNumber = phoneNumber.substring(countryCode.length, phoneNumber.length + 1);
    }
    return phoneNumber;
  }
}
